import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, Lock, User, Phone, ArrowRight, ArrowLeft, KeyRound, ShieldCheck, Check, X as XIcon } from "lucide-react";

function PasswordChecklist({ password }) {
  const checks = [
    { label: "8+ characters", pass: password.length >= 8 },
    { label: "Uppercase (A-Z)", pass: /[A-Z]/.test(password) },
    { label: "Lowercase (a-z)", pass: /[a-z]/.test(password) },
    { label: "Number (0-9)", pass: /[0-9]/.test(password) },
    { label: "Special (!@#$%^&*)", pass: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  ];
  if (!password) return null;
  return (
    <div className="mt-2 grid grid-cols-2 gap-1">
      {checks.map(c => (
        <div key={c.label} className={`flex items-center gap-1.5 text-xs ${c.pass ? "text-green-600" : "text-[#8A998E]"}`}>
          {c.pass ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />} {c.label}
        </div>
      ))}
    </div>
  );
}

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "login";
  const defaultRole = searchParams.get("role") || "customer";
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ identifier: "", password: "" });
  const [regForm, setRegForm] = useState({ email: "", password: "", name: "", phone: "", role: defaultRole });

  // Forgot password states
  const [forgotStep, setForgotStep] = useState(0); // 0=hidden, 1=enter email, 2=enter OTP+new pw
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetForm, setResetForm] = useState({ otp: "", new_password: "" });

  if (user) {
    const path = user.role === "admin" ? "/admin" : user.role === "host" ? "/host" : "/dashboard";
    navigate(path, { replace: true });
    return null;
  }

  const isAdminBuild = process.env.REACT_APP_MODE === "admin";

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(loginForm.identifier, loginForm.password);
      if (isAdminBuild && data.user.role !== "admin") {
        // Reject non-admins on admin portal
        localStorage.removeItem("ss_token");
        const target = data.user.role === "host" ? "https://extranet.safarsetu.com" : "https://safarsetu.com";
        toast.error(`This portal is for admins only. Redirecting to ${target}...`);
        setTimeout(() => { window.location.href = target; }, 1800);
        return;
      }
      toast.success(`Welcome back, ${data.user.name}!`);
      const path = data.user.role === "admin" ? "/admin" : data.user.role === "host" ? "/host" : "/dashboard";
      navigate(path);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await register(regForm);
      toast.success(`Welcome to SafarSetu, ${data.user.name}!`);
      const path = data.user.role === "host" ? "/host" : "/dashboard";
      navigate(path);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async () => {
    if (!forgotEmail) { toast.error("Please enter your email"); return; }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("Reset code sent to your email!");
      setForgotStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reset code");
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async () => {
    if (!resetForm.otp || !resetForm.new_password) { toast.error("Please fill all fields"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email: forgotEmail, ...resetForm });
      toast.success("Password reset successfully! Please login.");
      setForgotStep(0);
      setResetForm({ otp: "", new_password: "" });
      setForgotEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password UI
  if (forgotStep > 0) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#2C5F2D]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-7 h-7 text-[#2C5F2D]" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-[#1A231C] font-['Outfit'] mb-2">
              {forgotStep === 1 ? "Forgot Password" : "Reset Password"}
            </h1>
            <p className="text-sm text-[#5C6B60]">
              {forgotStep === 1 ? "Enter your email to receive a reset code" : `Enter the 6-digit code sent to ${forgotEmail}`}
            </p>
          </div>

          <div className="bg-white border border-[#E5E3DB] rounded-2xl p-6 shadow-sm">
            {forgotStep === 1 ? (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60] mb-1.5 block">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-4 h-4 text-[#8A998E]" />
                    <Input
                      type="email" placeholder="you@example.com"
                      className="pl-9 bg-white border-[#E5E3DB] rounded-xl h-11"
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button disabled={loading} className="w-full bg-[#2C5F2D] hover:bg-[#214A22] text-white rounded-full h-11" onClick={handleForgotSubmit} data-testid="send-otp-btn">
                  {loading ? "Sending..." : "Send Reset Code"} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60] mb-1.5 block">6-Digit Code</Label>
                  <div className="relative">
                    <ShieldCheck className="absolute left-3 top-3 w-4 h-4 text-[#8A998E]" />
                    <Input
                      placeholder="123456" maxLength={6}
                      className="pl-9 bg-white border-[#E5E3DB] rounded-xl h-11 font-mono tracking-[0.3em] text-center text-lg"
                      value={resetForm.otp}
                      onChange={e => setResetForm(f => ({ ...f, otp: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                      data-testid="otp-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60] mb-1.5 block">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 w-4 h-4 text-[#8A998E]" />
                    <Input
                      type="password" placeholder="Min 8 chars, A-Z, a-z, 0-9, !@#"
                      className="pl-9 bg-white border-[#E5E3DB] rounded-xl h-11"
                      value={resetForm.new_password}
                      onChange={e => setResetForm(f => ({ ...f, new_password: e.target.value }))}
                      data-testid="new-password-input"
                    />
                  </div>
                  <p className="text-xs text-[#8A998E] mt-1">Must include uppercase, lowercase, number & special character</p>
                </div>
                <Button disabled={loading} className="w-full bg-[#E07A5F] hover:bg-[#C96A50] text-white rounded-full h-11" onClick={handleResetSubmit} data-testid="reset-password-btn">
                  {loading ? "Resetting..." : "Reset Password"} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <button className="text-xs text-[#5C6B60] hover:text-[#2C5F2D] mx-auto block" onClick={() => { setForgotStep(1); setResetForm({ otp: "", new_password: "" }); }}>
                  Didn't receive code? Send again
                </button>
              </div>
            )}
            <button className="flex items-center gap-1.5 text-sm text-[#5C6B60] hover:text-[#2C5F2D] mt-4 mx-auto transition-colors" onClick={() => setForgotStep(0)} data-testid="back-to-login">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-[#1A231C] font-['Outfit'] mb-2">{isAdminBuild ? "Admin Portal" : "Welcome to SafarSetu"}</h1>
          <p className="text-sm text-[#5C6B60]">{isAdminBuild ? "Sign in with your admin credentials" : "Login or create an account to start booking"}</p>
        </div>

        {isAdminBuild && (
          <div className="mb-4 p-3 rounded-xl bg-[#FFF4E6] border border-[#E07A5F]/30 flex items-start gap-2.5" data-testid="admin-only-banner">
            <ShieldCheck className="w-4 h-4 text-[#E07A5F] mt-0.5 shrink-0" />
            <div className="text-xs text-[#7A4A33] leading-relaxed">
              <strong className="block text-[#5C2F1F] mb-0.5">For admin access only.</strong>
              Partners → <a href="https://extranet.safarsetu.com" className="underline font-medium">extranet.safarsetu.com</a> &nbsp;·&nbsp; Customers → <a href="https://safarsetu.com" className="underline font-medium">safarsetu.com</a>
            </div>
          </div>
        )}

        <div className="bg-white border border-[#E5E3DB] rounded-2xl p-6 shadow-sm">
          <Tabs defaultValue={defaultTab}>
            <TabsList className={`grid w-full ${isAdminBuild ? "grid-cols-1" : "grid-cols-2"} bg-[#F3F2EE] rounded-full mb-6 h-10`}>
              <TabsTrigger value="login" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm" data-testid="login-tab">
                Login
              </TabsTrigger>
              {!isAdminBuild && (
              <TabsTrigger value="register" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm" data-testid="register-tab">
                Register
              </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60] mb-1.5 block">Email or Mobile Number</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-4 h-4 text-[#8A998E]" />
                    <Input
                      required placeholder="email@example.com or +91 9876543210"
                      className="pl-9 bg-white border-[#E5E3DB] rounded-xl h-11 focus:ring-2 focus:ring-[#2C5F2D]/20 focus:border-[#2C5F2D]"
                      value={loginForm.identifier}
                      onChange={e => setLoginForm(f => ({ ...f, identifier: e.target.value }))}
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60] mb-1.5 block">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 w-4 h-4 text-[#8A998E]" />
                    <Input
                      type="password" required placeholder="Enter your password"
                      className="pl-9 bg-white border-[#E5E3DB] rounded-xl h-11 focus:ring-2 focus:ring-[#2C5F2D]/20 focus:border-[#2C5F2D]"
                      value={loginForm.password}
                      onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))}
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button type="button" className="text-xs text-[#E07A5F] hover:text-[#C96A50] font-medium transition-colors" onClick={() => setForgotStep(1)} data-testid="forgot-password-link">
                    Forgot Password?
                  </button>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#2C5F2D] hover:bg-[#214A22] text-white rounded-full h-11 font-medium" data-testid="login-submit-btn">
                  {loading ? "Logging in..." : "Login"} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60] mb-1.5 block">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 w-4 h-4 text-[#8A998E]" />
                    <Input
                      required placeholder="Your name"
                      className="pl-9 bg-white border-[#E5E3DB] rounded-xl h-11"
                      value={regForm.name}
                      onChange={e => setRegForm(f => ({ ...f, name: e.target.value }))}
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60] mb-1.5 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-4 h-4 text-[#8A998E]" />
                    <Input
                      type="email" required placeholder="you@example.com"
                      className="pl-9 bg-white border-[#E5E3DB] rounded-xl h-11"
                      value={regForm.email}
                      onChange={e => setRegForm(f => ({ ...f, email: e.target.value }))}
                      data-testid="register-email-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60] mb-1.5 block">Mobile Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 w-4 h-4 text-[#8A998E]" />
                    <Input
                      required placeholder="+91 9876543210"
                      className="pl-9 bg-white border-[#E5E3DB] rounded-xl h-11"
                      value={regForm.phone}
                      onChange={e => setRegForm(f => ({ ...f, phone: e.target.value }))}
                      data-testid="register-phone-input"
                    />
                  </div>
                  <p className="text-xs text-[#8A998E] mt-1">Used for login & WhatsApp confirmations</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60] mb-1.5 block">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 w-4 h-4 text-[#8A998E]" />
                    <Input
                      type="password" required placeholder="Create a strong password"
                      className="pl-9 bg-white border-[#E5E3DB] rounded-xl h-11"
                      value={regForm.password}
                      onChange={e => setRegForm(f => ({ ...f, password: e.target.value }))}
                      data-testid="register-password-input"
                    />
                  </div>
                  <PasswordChecklist password={regForm.password} />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60] mb-1.5 block">I am a</Label>
                  <Select value={regForm.role} onValueChange={v => setRegForm(f => ({ ...f, role: v }))}>
                    <SelectTrigger className="bg-white border-[#E5E3DB] rounded-xl h-11" data-testid="register-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Customer (Book Services)</SelectItem>
                      <SelectItem value="host">Host (List My Services)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#E07A5F] hover:bg-[#C96A50] text-white rounded-full h-11 font-medium" data-testid="register-submit-btn">
                  {loading ? "Creating account..." : "Create Account"} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
